'use client';

/**
 * Global error boundary — catches unhandled errors thrown inside the root
 * layout itself (e.g. a provider crash). Because the root layout is unmounted,
 * this component must render its own <html> and <body> tags.
 *
 * Distinct from app/error.tsx which handles errors in route segments while
 * keeping the root layout (sidebar, shell) mounted.
 *
 * Required for full Sentry React render error coverage under Next.js 15 /
 * Turbopack — sentry.client.config.ts no longer runs in this context.
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error#global-errorjs
 */

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    Sentry.captureException(error, { tags: { boundary: 'global' } });
  }, [error]);

  return (
    <html>
      <body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: '1.5rem',
            padding: '2rem',
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Something went wrong</h1>
          <p style={{ color: '#6b7280', maxWidth: '28rem', margin: 0 }}>
            A critical error occurred. The error has been reported automatically. Please refresh the
            page.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <pre
              style={{
                maxWidth: '40rem',
                overflow: 'auto',
                borderRadius: '0.375rem',
                background: '#fef2f2',
                padding: '1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                color: '#dc2626',
              }}
            >
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          )}
          <button
            onClick={reset}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
